import { getDefaultClassNames, type DayPickerProps } from '@daypicker/react';

const defaultDayPickerClassNames = getDefaultClassNames();

const createDayPickerClassNames = (
  isNarrowMobile: boolean
): DayPickerProps['classNames'] => ({
  ...defaultDayPickerClassNames,
  root: `${defaultDayPickerClassNames.root} !m-0 ![--rdp-accent-background-color:#dbeafe] ![--rdp-accent-color:#1d4ed8] ![--rdp-nav_button-height:2.5rem] ![--rdp-nav_button-width:2.5rem] ${
    isNarrowMobile
      ? '![--rdp-day-height:2.25rem] ![--rdp-day-width:2.25rem] ![--rdp-day_button-height:2rem] ![--rdp-day_button-width:2rem]'
      : '![--rdp-day-height:2.5rem] ![--rdp-day-width:2.5rem] ![--rdp-day_button-height:2.25rem] ![--rdp-day_button-width:2.25rem]'
  }`,
  months: `${defaultDayPickerClassNames.months} !max-w-none`,
  month: `${defaultDayPickerClassNames.month} ${
    isNarrowMobile ? '!min-w-[15.75rem]' : '!min-w-[17.5rem]'
  }`,
  month_caption: `${defaultDayPickerClassNames.month_caption} !text-sm !font-semibold`,
  weekday: `${defaultDayPickerClassNames.weekday} !text-xs !font-medium !text-[#737373]`,
  day_button: `${defaultDayPickerClassNames.day_button} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d4ed8]`,
  button_previous: `${defaultDayPickerClassNames.button_previous} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d4ed8]`,
  button_next: `${defaultDayPickerClassNames.button_next} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d4ed8]`,
});

const standardDayPickerClassNames = createDayPickerClassNames(false);
const narrowDayPickerClassNames = createDayPickerClassNames(true);

/** Tailwind classes shared by the date-range picker layout. */
export const dateRangePickerClasses = {
  panel: 'flex h-full w-full min-w-0 flex-col bg-white',
  panelBody: 'min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3 sm:px-5',
} as const;

/** The important layer wins over the shared popover's vendor z-index class. */
const popoverContentBaseClasses =
  'max-w-[calc(100vw-2rem)] overflow-hidden border border-neutral-100 bg-white !z-[2000]';

/** Anchor offset plus vertical border space reserved by the picker popover. */
export const DATE_RANGE_PICKER_POPOVER_GAP = 7;

/**
 * Returns the responsive Tailwind width and containment classes for the picker.
 *
 * @param showTwoMonths - Whether the desktop two-month layout is active.
 * @returns Tailwind classes for the popover content element.
 */
export const getDateRangePickerPopoverContentClasses = (
  showTwoMonths: boolean
): string =>
  `${popoverContentBaseClasses} ${
    showTwoMonths ? '!w-[44rem]' : '!w-[23.75rem]'
  }`;

/**
 * Returns DayPicker vendor classes merged with the picker Tailwind overrides.
 *
 * @param isNarrowMobile - Whether compact calendar dimensions are required.
 * @returns Class names for DayPicker's semantic slots.
 */
export const getDateRangePickerDayPickerClassNames = (
  isNarrowMobile: boolean
): DayPickerProps['classNames'] =>
  isNarrowMobile ? narrowDayPickerClassNames : standardDayPickerClassNames;
